import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ApexChart } from 'ng-apexcharts';
import { ChartOptions } from 'services/models/charts/ChartOptions';
import { FormMetricsCategoryChartData } from 'services/models/form-builder/form-metrics/chart-data/category-chart-data.model';
import { FormMetricsCategoryClickEvent } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsRespondentAnswerItemModel } from 'services/models/form-builder/form-metrics/form-metrics-drilldown.model';
import { FormMetricsItemModel } from 'services/models/form-builder/form-metrics/form-metrics-item.model';
import { FormMetricsChartMapperService } from '../../services/form-metrics-chart-mapper.service';
import { FormMetricsLegendItem } from '../../utils/form-metrics-legend.util';
import {
  applyHighlightColors,
  buildCategoryClickEvent,
  buildHighlightStroke,
  findHighlightForItem,
  FormMetricsSliceMeta,
  resolveHighlightedIndexes,
} from '../../utils/form-metrics-drilldown.util';

@Component({
  selector: 'app-form-metrics-bar-widget',
  templateUrl: './form-metrics-bar-widget.component.html',
  styleUrls: ['./form-metrics-bar-widget.component.scss'],
})
export class FormMetricsBarWidgetComponent implements OnChanges {
  @Input() item!: FormMetricsItemModel;
  @Input() categoryClickEnabled = false;
  @Input() highlightAnswers: FormMetricsRespondentAnswerItemModel[] = [];
  @Output() categoryClick = new EventEmitter<FormMetricsCategoryClickEvent>();

  chartOptions: Partial<ChartOptions> | null = null;
  legendItems: FormMetricsLegendItem[] = [];

  constructor(private chartMapper: FormMetricsChartMapperService) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.rebuild();
  }

  private rebuild(): void {
    if (!this.item || this.item.totalAnswers <= 0) {
      this.chartOptions = null;
      this.legendItems = [];
      return;
    }

    const mapped = this.chartMapper.mapBar(this.item);
    const data = this.item.chartData as FormMetricsCategoryChartData;
    const slices: FormMetricsSliceMeta[] = (data?.categories || []).map(c => ({
      optionId: c.optionId,
      label: c.label,
      value: null,
    }));
    const highlight = findHighlightForItem(this.highlightAnswers, this.item.formItemId);
    const highlightedIndexes = resolveHighlightedIndexes(slices, highlight);
    const baseColors = (mapped.chartOptions.colors as string[]) || [];

    const chart: ApexChart = {
      ...(mapped.chartOptions.chart as ApexChart),
    };
    if (this.categoryClickEnabled) {
      chart.events = {
        dataPointSelection: (_event, _ctx, config) => {
          const index = config?.dataPointIndex;
          const slice = slices[index];
          if (!slice) {
            return;
          }
          this.categoryClick.emit(
            buildCategoryClickEvent(this.item.formItemId, this.item.chartType, slice)
          );
        },
      };
    }

    this.chartOptions = {
      ...mapped.chartOptions,
      colors: applyHighlightColors(baseColors, highlightedIndexes),
      chart,
    };

    const stroke = buildHighlightStroke(baseColors, highlightedIndexes);
    if (stroke) {
      this.chartOptions.stroke = { width: stroke.width, colors: stroke.colors };
    }

    this.legendItems = mapped.legendItems;
  }
}
